import React from "react";

/**
 * Route components are code-split (see Routes/allRoutes.js), so the app now
 * fetches page chunks on demand. That introduces a failure mode the old single
 * bundle could not hit: after a deploy, a tab still running the previous
 * main.js asks for a hashed chunk that no longer exists on the server. Webpack
 * throws ChunkLoadError and, with no boundary above it, React unmounts the
 * whole tree and the user gets a white screen.
 *
 * This boundary catches exactly that case and reloads once to pick up the new
 * build.
 *
 * The guard is a TIMESTAMP, not a boolean. A boolean cleared on mount would
 * loop forever (fail -> reload -> mount clears the flag -> fail -> reload), and
 * a boolean never cleared would permanently disable the recovery after a single
 * unlucky network blip. A timestamp gives us both: a second failure inside the
 * window means reloading did not help, so we stop and show a message, while a
 * failure long afterwards - a later deploy - gets its own fresh reload.
 *
 * Anything that is not a chunk error is re-thrown, so ordinary component bugs
 * keep behaving exactly as they did before this boundary existed.
 */

const RELOAD_STAMP = "chunk_reload_at";
// Long enough to cover the reload plus the refetch, short enough that a genuine
// later deploy is never treated as the same incident.
const RELOAD_WINDOW_MS = 20000;

const isChunkError = (error) =>
  !!error &&
  (error.name === "ChunkLoadError" ||
    /Loading chunk [^\s]+ failed/i.test(error.message || "") ||
    /Loading CSS chunk [^\s]+ failed/i.test(error.message || ""));

// sessionStorage throws in some privacy modes, so never let it break the boundary.
const reloadedRecently = () => {
  try {
    const at = parseInt(sessionStorage.getItem(RELOAD_STAMP) || "0", 10);
    return !!at && Date.now() - at < RELOAD_WINDOW_MS;
  } catch (_) {
    // Storage unavailable: assume we already tried, so we show the message
    // rather than risk an unbounded reload loop.
    return true;
  }
};

const markReloaded = () => {
  try {
    sessionStorage.setItem(RELOAD_STAMP, String(Date.now()));
  } catch (_) {
    // ignore storage errors
  }
};

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (!isChunkError(error)) return;
    if (reloadedRecently()) return; // reloading did not fix it - show the message
    markReloaded();
    window.location.reload();
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // Not a chunk problem - preserve the pre-existing behaviour of letting the
    // error escape rather than silently swallowing a real bug.
    if (!isChunkError(error)) throw error;

    // A reload is in flight, or we already tried one and it did not help.
    return (
      <div className="d-flex flex-column align-items-center justify-content-center p-5 text-center">
        <h5>Updating to the latest version...</h5>
        <p className="text-muted mb-3">
          If this message stays on screen, refresh the page to continue.
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            try {
              sessionStorage.removeItem(RELOAD_STAMP);
            } catch (_) {
              // ignore storage errors
            }
            window.location.reload();
          }}
        >
          Refresh
        </button>
      </div>
    );
  }
}

export default ChunkErrorBoundary;
